import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/counselors.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

export default function MyProfile() {
  const { user, token, updateUser, authHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setFetching(true);
        const res = await fetch(`${API_URL}/auth/profile`, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'application/json',
          },
        });
        const data = await res.json();
        if (data.success && data.user) {
          setFormData((prev) => ({
            ...prev,
            name: data.user.name || '',
            email: data.user.email || '',
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            name: user?.name || '',
            email: user?.email || '',
          }));
        }
      } catch {
        setFormData((prev) => ({
          ...prev,
          name: user?.name || '',
          email: user?.email || '',
        }));
      } finally {
        setFetching(false);
      }
    };

    loadProfile();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required');
      return;
    }

    if (formData.password || formData.confirmPassword || formData.currentPassword) {
      if (!formData.currentPassword) {
        setError('Enter your current password to change password');
        return;
      }
      if (formData.password.length < 6) {
        setError('New password must be at least 6 characters');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('New password and confirmation do not match');
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
      };
      if (formData.password) {
        payload.password = formData.password;
        payload.currentPassword = formData.currentPassword;
      }

      const res = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success) {
        updateUser(data.user);
        setMessage(data.message || 'Profile updated successfully');
        setFormData((prev) => ({
          ...prev,
          currentPassword: '',
          password: '',
          confirmPassword: '',
        }));
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="form-container counselors-container">
          <div className="form-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1>
              <FontAwesomeIcon icon={faUser} className="page-icon" />
              My Profile
            </h1>
            <Link to="/dashboard" className="btn-secondary" style={{ textDecoration: 'none' }}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
            </Link>
          </div>

          {fetching ? (
            <p style={{ color: '#6b7280' }}>Loading profile...</p>
          ) : (
            <form onSubmit={handleSubmit} className="counselor-form">
              <div className="form-section">
                <h2 className="section-title">Account Details</h2>

                {message && (
                  <div style={{
                    background: '#ecfdf5',
                    color: '#065f46',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #a7f3d0',
                    fontSize: '14px',
                  }}>
                    {message}
                  </div>
                )}

                {error && (
                  <div style={{
                    background: '#fee2e2',
                    color: '#991b1b',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #fecaca',
                    fontSize: '14px',
                  }}>
                    {error}
                  </div>
                )}

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input-field"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email <span className="required">*</span></label>
                    <input
                      type="email"
                      className="input-field"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2 className="section-title">Change Password</h2>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                  Leave password fields blank if you do not want to change your password.
                </p>
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Current password</label>
                    <input
                      type="password"
                      className="input-field"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">New password</label>
                    <input
                      type="password"
                      className="input-field"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Confirm new password</label>
                    <input
                      type="password"
                      className="input-field"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <Link to="/dashboard" className="btn-secondary" style={{ textDecoration: 'none' }}>
                  Cancel
                </Link>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
